import {
  Duration,
  Locale,
  addDays,
  addHours,
  addMinutes,
  addMonths,
  addSeconds,
  addYears,
  secondsToMilliseconds,
  startOfDay,
} from 'date-fns';
import fnsDifferenceInDays from 'date-fns/differenceInDays';
import fnsDifferenceInSeconds from 'date-fns/differenceInSeconds';
import fnsFormat from 'date-fns/format';
import fnsFormatDistance from 'date-fns/formatDistance';
import fnsFormatDistanceStrict from 'date-fns/formatDistanceStrict';
import fnsFormatDuration from 'date-fns/formatDuration';
import fnsIntervalToDuration from 'date-fns/intervalToDuration';
import fnsIsAfter from 'date-fns/isAfter';
import fnsIsBefore from 'date-fns/isBefore';
import isEqual from 'date-fns/isEqual';
import { enUS, ko } from 'date-fns/locale';

const locales: Record<string, Locale> = { ko, en: enUS };

type DateLikeObject = Date | string | number;

function getLocale(): Locale {
  return typeof window !== 'undefined'
    ? locales[window.__localeId__]
    : locales[global.__localeId__];
}

/**
 * convert date like object into parse-able format
 * @param dateLikeObject Date, string or number in seconds
 * @returns Date | number
 */
function _parseDate(dateLikeObject: DateLikeObject) {
  if (typeof dateLikeObject === 'string') {
    return new Date(dateLikeObject);
  } else if (typeof dateLikeObject === 'number') {
    return new Date(secondsToMilliseconds(dateLikeObject));
  }
  return dateLikeObject;
}

export function formatDate(
  dateLikeObject: DateLikeObject,
  pattern = 'yyyy-MM-dd'
) {
  const date = _parseDate(dateLikeObject);
  const locale = getLocale();
  return fnsFormat(date, pattern, { locale });
}

export function formatDistance(
  dateLikeObject: DateLikeObject,
  baseDateOrString: DateLikeObject = new Date(),
  options?: Parameters<typeof fnsFormatDistance>[2]
): string {
  const date = _parseDate(dateLikeObject);
  const baseDate = _parseDate(baseDateOrString);
  const locale = getLocale();
  return fnsFormatDistance(date, baseDate, {
    locale,
    addSuffix: true,
    ...options,
  });
}

export function formatDistanceStrict(
  dateLikeObject: DateLikeObject,
  baseDateOrString: DateLikeObject = new Date(),
  options?: Parameters<typeof fnsFormatDistanceStrict>[2]
): string {
  const date = _parseDate(dateLikeObject);
  const baseDate = _parseDate(baseDateOrString);
  const locale = getLocale();
  return fnsFormatDistanceStrict(date, baseDate, {
    locale,
    roundingMethod: 'floor',
    addSuffix: true,
    ...options,
  });
}

export function formatDuration(
  dateLikeObject: DateLikeObject,
  startDateLikeObject: DateLikeObject = 0,
  options?: Parameters<typeof fnsFormatDuration>[1]
) {
  const duration = intervalToDuration(dateLikeObject, startDateLikeObject);
  const locale = getLocale();
  return fnsFormatDuration(duration, {
    locale,
    ...options,
  });
}

export function intervalToDuration(
  dateLikeObject: DateLikeObject,
  startDateLikeObject: DateLikeObject = 0
) {
  const start = _parseDate(startDateLikeObject);
  const end = _parseDate(dateLikeObject);
  return fnsIntervalToDuration({ start, end });
}

export function differenceInSeconds(
  dateLikeObject: DateLikeObject,
  dateLikeObject2: DateLikeObject
) {
  const dateLeft = _parseDate(dateLikeObject);
  const dateRight = _parseDate(dateLikeObject2);
  return fnsDifferenceInSeconds(dateLeft, dateRight);
}

export function differenceInDays(
  dateLikeObject: DateLikeObject,
  dateLikeObject2: DateLikeObject
) {
  const dateLeft = _parseDate(dateLikeObject);
  const dateRight = _parseDate(dateLikeObject2);
  return fnsDifferenceInDays(dateLeft, dateRight);
}

export function isAfter(
  dateLikeObject: DateLikeObject,
  compareDateLikeObject: DateLikeObject
) {
  const date = _parseDate(dateLikeObject);
  const compare = _parseDate(compareDateLikeObject);
  return fnsIsAfter(date, compare);
}

export function isBefore(
  dateLikeObject: DateLikeObject,
  compareDateLikeObject: DateLikeObject
) {
  const date = _parseDate(dateLikeObject);
  const compare = _parseDate(compareDateLikeObject);

  return fnsIsBefore(date, compare);
}

export function isBetween(
  fromDateLikeObject: DateLikeObject,
  toDateLikeObject: DateLikeObject,
  now: DateLikeObject = new Date()
) {
  return isAfter(now, fromDateLikeObject) && isBefore(now, toDateLikeObject);
}

export function isDateEqual(
  dateLikeObject: DateLikeObject,
  compareDateLikeObject: DateLikeObject
) {
  const date = _parseDate(dateLikeObject);
  const compare = _parseDate(compareDateLikeObject);

  return isEqual(getOnlyDate(date), getOnlyDate(compare));
}

export function getOnlyDate(dateLikeObject: DateLikeObject = new Date()) {
  const date = _parseDate(dateLikeObject);

  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

export function addDuration(
  dateLikeObject: DateLikeObject,
  duration: Duration
) {
  const durationHandler = {
    years: addYears,
    months: addMonths,
    days: addDays,
    hours: addHours,
    minutes: addMinutes,
    seconds: addSeconds,
  };
  let date = _parseDate(dateLikeObject);

  (Object.keys(durationHandler) as (keyof typeof durationHandler)[]).forEach(
    (key) => {
      const value = duration[key];
      if (value) {
        date = durationHandler[key](date, value);
      }
    }
  );

  return date;
}

export const resetHours = (date: DateLikeObject) => {
  return startOfDay(_parseDate(date));
};

const padZero = (num: number) => (num < 10 ? `0${num}` : num);

export const formatSecondsToTimer = (time: number, hideHours = false) => {
  const hours = Math.floor(time / 3600);
  const minutes = Math.floor((time % 3600) / 60);
  const seconds = time % 60;

  if (hideHours) {
    return `${padZero(minutes)}:${padZero(seconds)}`;
  }

  return `${padZero(hours)}:${padZero(minutes)}:${padZero(seconds)}`;
};

export const millisecondsToHHMM = (milliseconds: number) => {
  const totalSeconds = Math.floor(milliseconds / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);

  return `${padZero(hours)}:${padZero(minutes)}`;
};

export const extractMonthAndDay = (dateTimeString: string): string => {
  const date = new Date(dateTimeString);

  const month = date.getMonth() + 1;
  const day = date.getDate();

  return `${month}월 ${day}일`;
};

export function getDatesByDayOfWeek(
  startDate: Date,
  endDate: Date,
  dayOfWeek: number
): Date[] {
  const dates: Date[] = [];

  // 시작 날짜가 요일에 맞을 때까지 이동
  while (startDate.getDay() !== dayOfWeek) {
    startDate.setDate(startDate.getDate() + 1);
  }

  // 시작 날짜부터 종료 날짜까지 지정된 요일의 날짜를 찾기
  for (
    let date = new Date(startDate);
    date <= endDate;
    date.setDate(date.getDate() + 7)
  ) {
    dates.push(new Date(date));
  }

  return dates;
}

export const getYearAndMonth = (current: string | Date) => {
  const currentYear = new Date(current).getFullYear();
  const currentMonth = new Date(current).getMonth() + 1;

  return `${currentYear}-${padZero(currentMonth)}`;
};

export const calculateTimeDifference = (
  date1: string | Date,
  date2: string | Date
) => {
  const timeDifference = new Date(date2).getTime() - new Date(date1).getTime();

  if (timeDifference === 0) {
    return '0일 0시간';
  }

  const absTimeDifference = Math.abs(timeDifference);

  const days = Math.floor(absTimeDifference / (1000 * 60 * 60 * 24));
  const hours = Math.floor(
    (absTimeDifference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
  );

  return `${days}일 ${hours}시간`;
};

export function getDurationFromNextKST(hour: number, min: number) {
  const now = new Date();
  const nowInKST = _parseDate(now);

  const next18InKST = new Date(
    nowInKST.getFullYear(),
    nowInKST.getMonth(),
    nowInKST.getDate(),
    hour,
    min,
    0,
    0
  );

  function format(start: Date, end: Date): string {
    const duration = intervalToDuration(start, end);
    return `${duration.hours}시간 ${duration.minutes}분`;
  }

  if (nowInKST > next18InKST) {
    next18InKST.setDate(next18InKST.getDate() + 1);
  }

  return format(nowInKST, next18InKST);
}

export const getCurrentMonthKST = (): number => {
  const now = new Date();

  // 한국 시간대로 변환 (UTC+9)
  const koreaTimeOffset = 9 * 60; // 분 단위로 오프셋 계산
  const localTime = new Date(now.getTime() + koreaTimeOffset * 60 * 1000);

  // 월을 가져오며, JavaScript의 월은 0부터 시작하므로 1을 더해줌
  const month = localTime.getMonth() + 1;

  return month;
};
